import { useCallback, useRef, useState } from 'react';
import { trpc, type RouterOutputs } from '@org/clients/school';
import { Badge, Button, ConfirmDialog, GenericModal } from '@org/ui';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  NotificationBroadcastForm,
  type NotificationBroadcastFormValues,
} from '../forms/notification-broadcast';

type Broadcast = RouterOutputs['notifications']['listBroadcasts'][number];

function formatSentAt(value: string | Date): string {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }).format(new Date(value));
}

function senderName(broadcast: Broadcast): string {
  return [broadcast.createdByFirstName, broadcast.createdByLastName].filter(Boolean).join(' ') || '-';
}

/**
 * "Notifications" tab of the Announcements page: the list of messages already
 * pushed to the student-app bell, plus a modal to send a new one. Sending is
 * irreversible, so the form hands its values to a confirmation dialog first.
 */
export function NotificationBroadcastsTab() {
  const { t } = useTranslation('admin');
  const utils = trpc.useUtils();
  const formRef = useRef<HTMLFormElement>(null);

  const [isComposerOpen, setIsComposerOpen] = useState(false);
  const [pending, setPending] = useState<NotificationBroadcastFormValues | null>(null);

  const { data: broadcasts, isLoading } = trpc.notifications.listBroadcasts.useQuery({ limit: 100 });

  const sendMutation = trpc.notifications.sendBroadcast.useMutation({
    onSuccess: async (result) => {
      await utils.notifications.listBroadcasts.invalidate();
      toast.success(t('announcements.notifications.toasts.sent', { count: result.recipientCount }));
      setPending(null);
      setIsComposerOpen(false);
    },
    onError: () => {
      toast.error(t('announcements.notifications.toasts.error'));
    },
  });

  const searchGroups = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.groups.searchActiveGroups.fetch({ search: query });
      return results.map((g) => ({ value: g.groupId, label: g.description ?? g.groupId }));
    },
    [utils],
  );

  const searchUsers = useCallback(
    async (query: string) => {
      if (query.length < 3) return [];
      const results = await utils.users.searchActiveUsers.fetch({ search: query });
      return results.map((u) => ({ value: u.userId, label: `${u.firstName} ${u.lastName}` }));
    },
    [utils],
  );

  const handleConfirmSend = () => {
    if (!pending) return;
    sendMutation.mutate({
      title: pending.title,
      body: pending.body,
      link: pending.link,
      audience: pending.audience,
      targetIds: pending.targetIds,
    });
  };

  const confirmDescription = pending
    ? pending.audience === 'all'
      ? t('announcements.notifications.confirm.all')
      : t(`announcements.notifications.confirm.${pending.audience}`, { count: pending.targetIds.length })
    : null;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{t('announcements.notifications.subtitle')}</p>
        <Button variant="outline" onClick={() => setIsComposerOpen(true)}>
          {t('announcements.notifications.createButton')}
        </Button>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">Loading...</div>
      ) : !broadcasts || broadcasts.length === 0 ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('announcements.notifications.list.empty')}
        </div>
      ) : (
        <div className="rounded-lg border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.title')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.message')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.link')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.audience')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.recipients')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.sentBy')}</th>
                <th className="px-4 py-3 text-left font-medium">{t('announcements.notifications.list.sentAt')}</th>
              </tr>
            </thead>
            <tbody>
              {broadcasts.map((broadcast) => (
                <tr key={broadcast.broadcastId} className="border-b">
                  <td className="px-4 py-3 font-medium">{broadcast.title}</td>
                  <td className="max-w-md px-4 py-3 text-muted-foreground">
                    <span className="line-clamp-2">{broadcast.body}</span>
                  </td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{broadcast.link ?? '-'}</td>
                  <td className="px-4 py-3">
                    <Badge variant="outline">
                      {t(`announcements.notifications.audienceLabels.${broadcast.audience}`)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-center">{broadcast.recipientCount}</td>
                  <td className="px-4 py-3">{senderName(broadcast)}</td>
                  <td className="px-4 py-3 text-xs text-muted-foreground">{formatSentAt(broadcast.createdAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <GenericModal
        show={isComposerOpen}
        onClose={() => setIsComposerOpen(false)}
        title={t('announcements.notifications.form.createTitle')}
        size="lg"
        centered
        body={
          <NotificationBroadcastForm
            formRef={formRef}
            onSubmit={(values) => setPending(values)}
            onClose={() => setIsComposerOpen(false)}
            isSubmitting={sendMutation.isPending}
            onSearchGroups={searchGroups}
            onSearchUsers={searchUsers}
            needTargetsMessage={t('announcements.notifications.form.needTargets')}
            labels={{
              title: t('announcements.notifications.form.title'),
              body: t('announcements.notifications.form.body'),
              link: t('announcements.notifications.form.link'),
              linkHelp: t('announcements.notifications.form.linkHelp'),
              audience: t('announcements.notifications.form.audience'),
              audienceAll: t('announcements.notifications.form.audienceAll'),
              audienceGroups: t('announcements.notifications.form.audienceGroups'),
              audienceUsers: t('announcements.notifications.form.audienceUsers'),
              groups: t('announcements.notifications.form.groups'),
              users: t('announcements.notifications.form.users'),
              groupSearchPlaceholder: t('announcements.notifications.form.groupSearchPlaceholder'),
              userSearchPlaceholder: t('announcements.notifications.form.userSearchPlaceholder'),
              noGroups: t('announcements.notifications.form.noGroups'),
              noUsers: t('announcements.notifications.form.noUsers'),
            }}
          />
        }
        footer={
          <div className="flex w-full justify-end gap-2">
            <Button variant="outline" onClick={() => setIsComposerOpen(false)} disabled={sendMutation.isPending}>
              {t('announcements.notifications.form.cancel')}
            </Button>
            <Button onClick={() => formRef.current?.requestSubmit()} disabled={sendMutation.isPending}>
              {t('announcements.notifications.form.submit')}
            </Button>
          </div>
        }
      />

      <ConfirmDialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open && !sendMutation.isPending) setPending(null);
        }}
        onConfirm={handleConfirmSend}
        title={t('announcements.notifications.confirm.title')}
        description={confirmDescription}
        confirmLabel={t('announcements.notifications.confirm.confirmLabel')}
      />
    </div>
  );
}
